import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import InstallAppButton from "./InstallAppButton";
import SessionsClock from "../flexx/SessionsClock";

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg: "#060606",
  surface: "#0C0C0C",
  panel: "#111111",
  border: "#1C1C1C",
  borderHover: "#2A2A2A",
  gold: "#C9A227",
  text: "#EDE8DC",
  mute: "#AAAAAA",
  muted: "#666666",
  red: "#FF4444",
};

const DESKTOP_BREAKPOINT = 900;

const TABS = [
  { id: "home",    label: "Home",    icon: "🏠", to: "/" },
  { id: "learn",   label: "Learn",   icon: "📚", to: "/learn" },
  { id: "intel",   label: "Intel",   icon: "🧠", to: "/intel" },
  { id: "live",    label: "Live",    icon: "🔴", to: "/live", isLiveTab: true },
  { id: "library", label: "Hub",     icon: "📚", to: "/library" },
  { id: "journal", label: "Journal", icon: "📓", to: "/#journal" },
  { id: "tools",   label: "Tools",   icon: "🧰", to: "/tools" },
  { id: "profile", label: "Profile", icon: "👤", to: "/#profile" },
];

const HIDDEN_ROUTES = ["/reset-password"];

/**
 * BottomNav — global navigation wrapper
 *   - Mobile (< DESKTOP_BREAKPOINT px): fixed bottom-tab bar (5 tabs)
 *   - Desktop (≥ DESKTOP_BREAKPOINT): 240px fixed left sidebar
 *   - Hidden on public routes (reset password, cert /verify/:id, landing)
 *   - Live tab pulses red when /api/live/state.is_live === true
 */
export default function BottomNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= DESKTOP_BREAKPOINT);
  const [isLive, setIsLive] = useState(false);
  const [alertFlash, setAlertFlash] = useState(false);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Poll live state every 30s. Detects (a) is_live status, (b) new alert pushed.
  useEffect(() => {
    if (!user) return undefined;
    let mounted = true;
    let flashTimer = null;
    const onLivePage = () => location.pathname.startsWith("/live");

    const fetchLive = async () => {
      try {
        const token = localStorage.getItem("flexx_access_token");
        if (!token) return;
        const res = await axios.get(`${API}/api/live/state`, { headers: { Authorization: `Bearer ${token}` } });
        if (!mounted) return;
        setIsLive(!!res.data?.is_live);

        // New-alert detection: compare top alert string against last-seen in localStorage.
        const top = (res.data?.alerts || [])[0] || "";
        const lastSeen = localStorage.getItem("flexx_last_seen_alert");
        // First-ever load: just store it, don't flash.
        if (lastSeen === null) {
          localStorage.setItem("flexx_last_seen_alert", top);
        } else if (top && top !== lastSeen) {
          localStorage.setItem("flexx_last_seen_alert", top);
          // Skip flash if user is already viewing the Live page.
          if (!onLivePage()) {
            setAlertFlash(true);
            if (flashTimer) clearTimeout(flashTimer);
            flashTimer = setTimeout(() => mounted && setAlertFlash(false), 12_000);
          }
        }
      } catch (_) { /* silent */ }
    };
    fetchLive();
    const i = setInterval(fetchLive, 30_000);
    return () => { mounted = false; clearInterval(i); if (flashTimer) clearTimeout(flashTimer); };
  }, [user, location.pathname]);

  // Auto-clear flash when user navigates to /live.
  useEffect(() => {
    if (location.pathname.startsWith("/live") && alertFlash) setAlertFlash(false);
  }, [location.pathname, alertFlash]);

  // Don't render if not logged in, or on public/landing routes, or on /verify/:id
  if (!user) return null;
  if (HIDDEN_ROUTES.includes(location.pathname)) return null;
  if (location.pathname.startsWith("/verify/")) return null;

  const isActive = (tab) => {
    if (tab.to === "/") return location.pathname === "/" && !location.hash;
    if (tab.to.startsWith("/#")) {
      const hash = tab.to.split("#")[1];
      return location.pathname === "/" && location.hash === `#${hash}`;
    }
    return location.pathname.startsWith(tab.to);
  };

  const go = (tab) => {
    if (tab.to.startsWith("/#")) {
      navigate("/");
      // After navigation, set the hash so FlexxLearningHub catches it
      setTimeout(() => {
        window.location.hash = tab.to.split("#")[1];
      }, 10);
    } else {
      navigate(tab.to);
    }
  };

  return isDesktop ? (
    <DesktopSidebar tabs={TABS} isActive={isActive} onGo={go} isLive={isLive} alertFlash={alertFlash} />
  ) : (
    <MobileBottomBar tabs={TABS} isActive={isActive} onGo={go} isLive={isLive} alertFlash={alertFlash} />
  );
}

function MobileBottomBar({ tabs, isActive, onGo, isLive, alertFlash }) {
  const [showInstallTile, setShowInstallTile] = useState(() =>
    typeof window !== "undefined" && !window.matchMedia?.("(display-mode: standalone)")?.matches
  );
  useEffect(() => {
    const onInstalled = () => setShowInstallTile(false);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onMq = () => setShowInstallTile(!(mq?.matches));
    mq?.addEventListener?.("change", onMq);
    return () => {
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onMq);
    };
  }, []);
  const cols = `repeat(${tabs.length + (showInstallTile ? 1 : 0)}, 1fr)`;
  return (
    <nav data-testid="bottom-nav-mobile" role="navigation" aria-label="Primary"
      style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 100,
        background: "rgba(6,6,6,0.96)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderTop: `1px solid ${T.border}`,
        display: "grid", gridTemplateColumns: cols,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}>
      {tabs.map((t) => <TabButton key={t.id} tab={t} active={isActive(t)} isLive={isLive} alertFlash={alertFlash} onClick={() => onGo(t)} layout="mobile" />)}
      {showInstallTile && <InstallAppButton variant="tile" />}
    </nav>
  );
}

function DesktopSidebar({ tabs, isActive, onGo, isLive, alertFlash }) {
  return (
    <>
      <aside data-testid="bottom-nav-sidebar" role="navigation" aria-label="Primary"
        style={{
          position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 100,
          width: 240, padding: "70px 12px 24px",
          background: T.surface,
          borderRight: `1px solid ${T.border}`,
          display: "flex", flexDirection: "column", gap: 4,
        }}>
        <div style={{ padding: "0 12px 16px", borderBottom: `1px solid ${T.border}`, marginBottom: 12 }}>
          <div style={{ fontSize: 9, letterSpacing: 2.5, color: T.gold, fontFamily: "monospace" }}>FLEXX ACADEMY</div>
          <div style={{ fontSize: 11, color: T.mute, marginTop: 4, fontFamily: "monospace" }}>NAVIGATION</div>
          <div data-testid="sidebar-sessions-pill" style={{ marginTop: 12 }}>
            <SessionsClock compact />
          </div>
        </div>
        {tabs.map((t) => <TabButton key={t.id} tab={t} active={isActive(t)} isLive={isLive} alertFlash={alertFlash} onClick={() => onGo(t)} layout="desktop" />)}
        {/* Sidebar footer — discoverable install entry from any screen */}
        <div style={{ marginTop: "auto", padding: "12px 4px 0", borderTop: `1px solid ${T.border}` }}>
          <InstallAppButton />
        </div>
      </aside>
      {/* Content spacer — every authenticated page shifts right by 240px on desktop.
          overflow-x:hidden prevents absolute-positioned ambient decorations on
          legacy pages (e.g. Home .section-glow / .particle) from creating a
          horizontal scrollbar after the body padding shift. */}
      <style>{`@media (min-width: ${DESKTOP_BREAKPOINT}px) {
        body { padding-left: 240px; }
        html, body { overflow-x: hidden; }
      }`}</style>
    </>
  );
}

function TabButton({ tab, active, isLive, alertFlash, onClick, layout }) {
  const isLiveTab = tab.isLiveTab && isLive;
  const isAlertingTab = tab.isLiveTab && alertFlash;
  const desktop = layout === "desktop";
  return (
    <button
      data-testid={`bottom-nav-${tab.id}`}
      onClick={onClick}
      className={isAlertingTab ? "flexx-pulse-gold" : ""}
      style={{
        all: "unset",
        display: "flex",
        alignItems: "center",
        gap: desktop ? 12 : 0,
        flexDirection: desktop ? "row" : "column",
        justifyContent: desktop ? "flex-start" : "center",
        padding: desktop ? "12px 14px" : "10px 6px 8px",
        borderRadius: desktop ? 8 : 0,
        cursor: "pointer",
        color: active ? T.gold : (isAlertingTab ? T.gold : (isLiveTab ? T.red : T.mute)),
        background: desktop && active ? T.gold + "18" : (isAlertingTab ? T.gold + "12" : "transparent"),
        borderLeft: desktop && active ? `3px solid ${T.gold}` : (desktop && isAlertingTab ? `3px solid ${T.gold}` : "3px solid transparent"),
        boxShadow: isAlertingTab ? `0 0 18px ${T.gold}66, inset 0 0 0 1px ${T.gold}55` : "none",
        minHeight: 48,
        transition: "color 0.15s, background 0.15s, box-shadow 0.2s",
      }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: desktop ? 18 : 22, lineHeight: 1, filter: active ? "none" : "grayscale(30%)" }}>{tab.icon}</span>
        {isLiveTab && (
          <span data-testid={`bottom-nav-${tab.id}-pulse`}
            className="flexx-pulse-red"
            style={{
              position: "absolute", top: -2, right: -4,
              width: 8, height: 8, borderRadius: "50%",
              background: T.red, display: "inline-block",
            }} />
        )}
        {isAlertingTab && !isLiveTab && (
          <span data-testid={`bottom-nav-${tab.id}-alert-dot`}
            className="flexx-pulse-gold-dot"
            style={{
              position: "absolute", top: -2, right: -4,
              width: 8, height: 8, borderRadius: "50%",
              background: T.gold, display: "inline-block",
            }} />
        )}
      </div>
      <span style={{
        fontSize: desktop ? 13 : 10,
        fontFamily: "monospace",
        letterSpacing: desktop ? 1 : 0.5,
        fontWeight: 700,
        marginTop: desktop ? 0 : 4,
      }}>{tab.label}</span>
    </button>
  );
}
